import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Field } from "react-final-form";
import classNames from "classnames";
import { Checkmark, Warning } from "../icons";
import "./FormTextArea.scss";

const DEFAULT_MAX_LENGTH = 450;

const FormTextArea = forwardRef(({
  name,
  labelRenderer,
  initialValue,
  placeholder = '',
  disabled = false,
  required = false,
  accept = false,
  reject = false,
  maxlength = DEFAULT_MAX_LENGTH,
  className = null,
  // Remaining props like 'validate' or 'value'
  ...rest
}, ref) => {
  const inputId = `form-textarea--${name}`;

  return (
    <label
      htmlFor={inputId}
      className={classNames('form-textarea', className, {
        'form-textarea--disabled': disabled,
        'form-textarea--accept': accept,
        'form-textarea--reject': reject
      })}
    >
      {typeof labelRenderer === 'function' && labelRenderer()}
      
      <div className="form-textarea__container">
        <Field
          name={name}
          initialValue={initialValue}
          // 1. Using a render prop to access 'input' state directly
          subscription={{ value: true, error: true, touched: true }}
        >
          {({ input }) => {
            const currentLength = input.value ? input.value.length : 0;

            return (
              <>
                <textarea
                  {...input}
                  {...rest}
                  id={inputId}
                  ref={ref}
                  rows="4"
                  className="form-textarea__control"
                  placeholder={placeholder}
                  disabled={disabled}
                  required={required}
                  maxLength={maxlength}
                />
                
                {/* 2. Character counter updates instantly as user types */}
                <div className="form-textarea__limit">
                  {`${currentLength} / ${maxlength}`}
                </div>
              </>
            );
          }}
        </Field>

        {/* 3. Conditional icons using logical AND */}
        {accept && (
          <Checkmark
            className="form-textarea__icon"
            width="2.4rem"
            height="2.4rem"
          />
        )}
        
        {reject && (
          <Warning
            className="form-textarea__icon"
            width="2.4rem"
            height="2.4rem"
          />
        )}
      </div>
    </label>
  );
});

FormTextArea.propTypes = {
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  accept: PropTypes.bool,
  reject: PropTypes.bool,
  labelRenderer: PropTypes.func,
  className: PropTypes.string,
  maxlength: PropTypes.number,
  initialValue: PropTypes.string
};

export default FormTextArea;
